import type { LottieRefCurrentProps, LottieComponentProps } from 'lottie-react';
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  cloneElement,
} from 'react';

type LottieCompatibleComponent = ReactElement<LottieComponentProps>;

type Props = {
  children: LottieCompatibleComponent;
};

const THRESHOLD = 0.1;
const ROOT_MARGIN = '0px';

export const LottieAnimateOnView = forwardRef<HTMLDivElement, Props>(
  ({ children }, ref) => {
    const [hasPlayed, setHasPlayed] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<LottieRefCurrentProps>(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasPlayed) {
            setHasPlayed(true);
            if (animationRef.current) {
              animationRef.current.play();
            }
          } else if (!entry.isIntersecting) {
            setHasPlayed(false);
            if (animationRef.current) {
              animationRef.current.stop();
            }
          }
        },
        { threshold: THRESHOLD, rootMargin: ROOT_MARGIN },
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => observer.disconnect();
    }, [hasPlayed]);

    const enhancedChild = cloneElement(children, {
      ...children.props,
      lottieRef: animationRef,
    });

    return <div ref={ref || containerRef}>{enhancedChild}</div>;
  },
);
